/**
 * Timezone Utilities
 * 
 * Provides functions to handle timezone conversions for store hours display.
 * Maps US cities/states to their IANA timezone identifiers.
 */

// US State to primary timezone mapping
// Note: Some states span multiple timezones; we use the most common one
const STATE_TIMEZONE_MAP: Record<string, string> = {
  // Eastern Time
  'CT': 'America/New_York',
  'DC': 'America/New_York',
  'DE': 'America/New_York',
  'FL': 'America/New_York', // Most of FL is Eastern
  'GA': 'America/New_York',
  'IN': 'America/Indiana/Indianapolis', // Most of IN is Eastern
  'KY': 'America/New_York', // Eastern KY
  'MA': 'America/New_York',
  'MD': 'America/New_York',
  'ME': 'America/New_York',
  'MI': 'America/Detroit',
  'NC': 'America/New_York',
  'NH': 'America/New_York',
  'NJ': 'America/New_York',
  'NY': 'America/New_York',
  'OH': 'America/New_York',
  'PA': 'America/New_York',
  'RI': 'America/New_York',
  'SC': 'America/New_York',
  'VA': 'America/New_York',
  'VT': 'America/New_York',
  'WV': 'America/New_York',
  
  // Central Time
  'AL': 'America/Chicago',
  'AR': 'America/Chicago',
  'IA': 'America/Chicago',
  'IL': 'America/Chicago',
  'KS': 'America/Chicago', // Most of KS
  'LA': 'America/Chicago',
  'MN': 'America/Chicago',
  'MO': 'America/Chicago',
  'MS': 'America/Chicago',
  'ND': 'America/Chicago', // Most of ND
  'NE': 'America/Chicago', // Most of NE
  'OK': 'America/Chicago',
  'SD': 'America/Chicago', // Most of SD
  'TN': 'America/Chicago', // Most of TN
  'TX': 'America/Chicago', // Most of TX
  'WI': 'America/Chicago',
  
  // Mountain Time
  'AZ': 'America/Phoenix', // No DST
  'CO': 'America/Denver',
  'MT': 'America/Denver',
  'NM': 'America/Denver',
  'UT': 'America/Denver',
  'WY': 'America/Denver',
  
  // Pacific Time
  'CA': 'America/Los_Angeles',
  'NV': 'America/Los_Angeles',
  'OR': 'America/Los_Angeles',
  'WA': 'America/Los_Angeles',
  
  // Alaska
  'AK': 'America/Anchorage',
  
  // Hawaii
  'HI': 'Pacific/Honolulu',
  
  // US Territories
  'PR': 'America/Puerto_Rico',
  'VI': 'America/Virgin',
  'GU': 'Pacific/Guam',
  'AS': 'Pacific/Pago_Pago',
};

// Special city overrides for cities in split-timezone states
const CITY_TIMEZONE_OVERRIDES: Record<string, string> = {
  // Florida panhandle (Central Time)
  'pensacola_fl': 'America/Chicago',
  'panama city_fl': 'America/Chicago',
  
  // Indiana exceptions
  'gary_in': 'America/Chicago',
  'hammond_in': 'America/Chicago',
  
  // Kentucky western (Central Time)
  'bowling green_ky': 'America/Chicago',
  'owensboro_ky': 'America/Chicago',
  
  // Tennessee eastern (Eastern Time)
  'knoxville_tn': 'America/New_York',
  'chattanooga_tn': 'America/New_York',
  
  // Texas western (Mountain Time)
  'el paso_tx': 'America/Denver',
};

/**
 * Get the IANA timezone identifier for a given location
 */
export function getTimezoneForLocation(
  city?: string | null,
  state?: string | null,
  country?: string | null
): string {
  // Default to user's local timezone if we can't determine store's timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // For now, we only handle US locations
  if (country && country.toUpperCase() !== 'US' && country.toUpperCase() !== 'USA') {
    return userTimezone;
  }
  
  // Check for city-specific override
  if (city && state) {
    const cityKey = `${city.toLowerCase()}_${state.toLowerCase()}`;
    if (CITY_TIMEZONE_OVERRIDES[cityKey]) {
      return CITY_TIMEZONE_OVERRIDES[cityKey];
    }
  }
  
  // Use state mapping
  if (state) {
    const stateUpper = state.toUpperCase();
    if (STATE_TIMEZONE_MAP[stateUpper]) {
      return STATE_TIMEZONE_MAP[stateUpper];
    }
  }
  
  // Default to user's timezone
  return userTimezone;
}

/**
 * Get the user's local timezone
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Check if two timezones are currently in the same offset
 * (They might be in DST at different times)
 */
export function areTimezonesEqual(tz1: string, tz2: string): boolean {
  if (tz1 === tz2) return true;
  
  const now = new Date();
  const offset1 = getTimezoneOffset(now, tz1);
  const offset2 = getTimezoneOffset(now, tz2);
  
  return offset1 === offset2;
}

/**
 * Get timezone offset in minutes for a given date and timezone
 */
function getTimezoneOffset(date: Date, timezone: string): number {
  try {
    if (!timezone || !date || isNaN(date.getTime())) {
      return 0;
    }
    
    const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' });
    const tzStr = date.toLocaleString('en-US', { timeZone: timezone });
    
    const utcDate = new Date(utcStr);
    const tzDate = new Date(tzStr);
    
    // Validate parsed dates
    if (isNaN(utcDate.getTime()) || isNaN(tzDate.getTime())) {
      return 0;
    }
    
    const offset = (utcDate.getTime() - tzDate.getTime()) / 60000;
    return isNaN(offset) ? 0 : offset;
  } catch {
    // Return 0 offset if timezone is invalid (treat as UTC)
    return 0;
  }
}

/**
 * Convert a time string (HH:MM) from one timezone to another
 * Returns the converted time in HH:MM format
 */
export function convertTime(
  timeStr: string,
  fromTimezone: string,
  toTimezone: string,
  referenceDate?: Date
): string {
  // Return original if no conversion needed or invalid input
  if (!timeStr || !fromTimezone || !toTimezone || fromTimezone === toTimezone) {
    return timeStr;
  }
  
  try {
    const parts = timeStr.split(':');
    if (parts.length < 2) {
      return timeStr;
    }
    
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return timeStr;
    }
    
    // Use today as reference date if not provided
    const refDate = referenceDate || new Date();
    
    // Create a date in the source timezone
    const year = refDate.getFullYear();
    const month = refDate.getMonth();
    const day = refDate.getDate();
    
    // Create date at the specified time in source timezone
    const sourceDate = new Date(year, month, day, hours, minutes, 0, 0);
    
    // Calculate the offset difference
    const fromOffset = getTimezoneOffset(sourceDate, fromTimezone);
    const toOffset = getTimezoneOffset(sourceDate, toTimezone);
    const offsetDiff = fromOffset - toOffset; // in minutes
    
    // Check for NaN in offset calculation
    if (isNaN(offsetDiff)) {
      return timeStr;
    }
    
    // Adjust the time
    const totalMinutes = hours * 60 + minutes + offsetDiff;
    
    // Handle day wraparound
    const adjustedMinutes = ((totalMinutes % 1440) + 1440) % 1440;
    const newHours = Math.floor(adjustedMinutes / 60);
    const newMinutes = adjustedMinutes % 60;
    
    // Final NaN check
    if (isNaN(newHours) || isNaN(newMinutes)) {
      return timeStr;
    }
    
    return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
  } catch {
    // Return original time if conversion fails
    return timeStr;
  }
}

/**
 * Get timezone abbreviation for display (e.g., "EST", "PST")
 */
export function getTimezoneAbbreviation(timezone: string, date?: Date): string {
  const refDate = date || new Date();
  
  try {
    // Format the date with the timezone to get the abbreviation
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(refDate);
    
    const tzPart = parts.find(part => part.type === 'timeZoneName');
    return tzPart?.value || timezone;
  } catch {
    return timezone;
  }
}

/**
 * Get a human-readable timezone difference description
 */
export function getTimezoneDifferenceText(
  storeTimezone: string,
  userTimezone: string
): string | null {
  if (storeTimezone === userTimezone) {
    return null;
  }
  
  const now = new Date();
  const storeOffset = getTimezoneOffset(now, storeTimezone);
  const userOffset = getTimezoneOffset(now, userTimezone);
  const diffHours = (storeOffset - userOffset) / 60;
  
  if (diffHours === 0) {
    return null;
  }
  
  const absDiff = Math.abs(diffHours);
  const direction = diffHours > 0 ? 'behind' : 'ahead';
  
  if (absDiff === 1) {
    return `Store is 1 hour ${direction}`;
  } else {
    return `Store is ${absDiff} hours ${direction}`;
  }
}

/**
 * Format time for display with optional timezone indicator
 */
export function formatTimeWithTimezone(
  timeStr: string,
  timezone: string,
  showTimezone: boolean = false
): string {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  
  let result = `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  
  if (showTimezone) {
    const tzAbbrev = getTimezoneAbbreviation(timezone);
    result += ` ${tzAbbrev}`;
  }
  
  return result;
}
